import { Component, OnInit, signal, ViewChild } from '@angular/core';
import { ChallengesFacade } from '../../state/challenges.facade';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { MonthlyDialogComponent } from '../../components/dialogs/monthly-dialog/monthly-dialog.component';
import { DailyDialogComponent } from '../../components/dialogs/daily-dialog/daily-dialog.component';

export enum EChallengeType {
  MONTHLY = 'MONTHLY',
  DAILY = 'DAILY',
  NONE = 'NONE',
}

@Component({
  selector: 'app-challenges',
  templateUrl: './challenges.page.html',
  styleUrls: ['./challenges.page.scss']
})
export class ChallengesPage implements OnInit {
  readonly ChallengeType = EChallengeType;

  readonly globalAdminChallenge$ = this.challengesFacade.globalChallenge$;
  readonly assignedAdminChallenge$ = this.challengesFacade.assignedChallenge$;
  readonly dailyUserChallenge$ = this.challengesFacade.dailyChallenge$;
  readonly monthlyUserChallenge$ = this.challengesFacade.monthlyChallenge$;
  readonly historyChallenges$ = this.challengesFacade.historyChallenges$;
  readonly isLoading$ = this.challengesFacade.isLoading$;

  readonly emptyChallenges$ = combineLatest([
    this.globalAdminChallenge$,
    this.assignedAdminChallenge$,
    this.dailyUserChallenge$,
    this.monthlyUserChallenge$,
    this.historyChallenges$,
  ]).pipe(
    map(([global, assigned, daily, monthly, history]) => {
      return global === undefined && assigned === undefined && daily === undefined && monthly === undefined && history === undefined;
    })
  );

  section = 'challenges';

  @ViewChild('monthlyDialog') monthlyDialog!: MonthlyDialogComponent;
  @ViewChild('dailyDialog') dailyDialog!: DailyDialogComponent;

  readonly deleteChallengeType = signal<EChallengeType>(EChallengeType.NONE);

  constructor(
    private readonly challengesFacade: ChallengesFacade,
  ) {
  }

  ngOnInit() {
    this.challengesFacade.getAllChallenges().subscribe();
    this.loadHistory();
  }

  loadHistory(page = 1) {
    this.challengesFacade.getChallengesHistory(page).subscribe();
  }

  createGoal(challengeType: EChallengeType) {
    if (challengeType === EChallengeType.MONTHLY) {
      this.createMonthlyDialog();
    } else if (challengeType === EChallengeType.DAILY) {
      this.createDailyDialog();
    }
  }

  updateGoal(challengeType: EChallengeType) {
    if (challengeType === EChallengeType.MONTHLY) {
      this.createMonthlyDialog(this.challengesFacade.monthlyChallenge.target);
    } else if (challengeType === EChallengeType.DAILY) {
      this.createDailyDialog(this.challengesFacade.dailyChallenge.target);
    }
  }

  deleteChallenge() {
    if (this.deleteChallengeType() === EChallengeType.MONTHLY) {
      this.challengesFacade.deleteUserMonthlyChallenge();
    } else if (this.deleteChallengeType() === EChallengeType.DAILY) {
      this.challengesFacade.deleteUserDailyChallenge();
    }

    this.deleteChallengeType.set(EChallengeType.NONE);
  }

  showDeleteChallengeModal(challengeType: EChallengeType) {
    this.deleteChallengeType.set(challengeType);
  }

  private createMonthlyDialog(initialValue?: number) {
    this.challengesFacade.setInitialValue(initialValue);
    this.monthlyDialog.dialog.openDialog();
  }

  private createDailyDialog(initialValue?: number) {
    this.challengesFacade.setInitialValue(initialValue);
    this.dailyDialog.dialog.openDialog();
  }
}
